/**
 * <lazy-video> — injects a deferred <video> (kept inside a <template>) only when
 * the element scrolls into the viewport. Elements hidden via CSS (e.g. the
 * desktop variant on mobile) never intersect, so the wrong-device file is never
 * downloaded. Marked-eager posters are prioritized in the Liquid snippet.
 */
if (!customElements.get('lazy-video')) {
  class LazyVideo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.loadVideo();
            });
          },
          { rootMargin: '200px 0px' }
        );
        this.observer.observe(this);
      } else {
        this.loadVideo();
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    loadVideo() {
      if (this.dataset.loaded) return;

      const template = this.querySelector('template');
      if (!template || !template.content.firstElementChild) return;

      this.dataset.loaded = 'true';
      if (this.observer) this.observer.disconnect();

      const media = template.content.firstElementChild.cloneNode(true);
      this.appendChild(media);

      const video = this.querySelector('video');
      if (!video) return;

      video.addEventListener(
        'loadeddata',
        () => this.setAttribute('data-playing', ''),
        { once: true }
      );

      if (video.hasAttribute('autoplay')) {
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt.catch(() => {
            /* autoplay blocked (e.g. reduced-data) — poster stays visible */
          });
        }
      }
    }
  }

  customElements.define('lazy-video', LazyVideo);
}
